import { For, type JSX, Show } from "solid-js";
import type {
	QuestionCoverage,
	TreeCapability,
	TreeQuestion,
	TreeTopic,
} from "../api/domain.ts";
import { useI18n } from "../i18n/provider.tsx";
import { anchorId } from "../lib/anchor.ts";
import type { Marks } from "../lib/catalog-search.ts";
import { formatWeight } from "../lib/format.ts";
import { Highlight } from "./Highlight.tsx";
import styles from "./TopicTree.module.scss";
import {
	type Folding,
	showsBranch,
	Twisty,
	useFolding,
} from "./tree-shell.tsx";

/**
 * How a report treats a question's answers, named for the reader rather than
 * for the enum. The three cases call for three different actions, which is why
 * the tree tells them apart instead of showing "no score" for all of them.
 */
const coverageStyle: Record<QuestionCoverage, string> = {
	counted: styles.coverageCounted ?? "",
	outside: styles.coverageOutside ?? "",
	unanswered: styles.coverageUnanswered ?? "",
};

export type TopicTreeProps = {
	topics: TreeTopic[];
	/**
	 * The folding state, when a screen wants to drive it from outside — the
	 * catalog puts fold-everything buttons in its own header. Left out, the tree
	 * keeps its own, so it folds on any screen without extra wiring.
	 */
	folding?: Folding;
	/**
	 * The characters a search hit in each row's title, keyed by the row's
	 * element id. Left out, titles render plain.
	 */
	marks?: Marks;
	/** Buttons that belong to one topic, such as its edit action. */
	topicActions?: (topic: TreeTopic) => JSX.Element;
	/** Buttons that belong to one question. Revealed on hover and on focus. */
	questionActions?: (question: TreeQuestion, topic: TreeTopic) => JSX.Element;
	/** Buttons that belong to one capability. Revealed on hover and on focus. */
	capabilityActions?: (
		capability: TreeCapability,
		topic: TreeTopic,
	) => JSX.Element;
};

/**
 * The CONCEPT.md topics with their questions and capabilities inline. Screens
 * pass render functions for the detail instead of rebuilding the markup.
 */
export const TopicTree = (props: TopicTreeProps) => {
	const i18n = useI18n();
	const own = useFolding(() => props.topics);
	const folding = (): Folding => props.folding ?? own;
	const marked = (kind: "topic" | "capability" | "question", id: string) =>
		props.marks?.get(anchorId({ kind, id }));

	const Branch = (branch: { topics: TreeTopic[] }) => (
		<ul class={styles.branch}>
			<For each={branch.topics}>
				{(topic) => (
					<li class={styles.node}>
						{/*
              Every row carries the id that addresses it, so a screen that
              reloads the tree can put the row it just wrote back on screen and
              a link can point at one from outside.
            */}
						<div
							class={styles.topic}
							id={anchorId({ kind: "topic", id: topic.id })}
						>
							<div class={styles.topicHead}>
								<Twisty topic={topic} folding={folding()} />
								<span class={styles.count}>{topic.questions.length}</span>
								<h3 class={styles.topicTitle}>
									<Highlight
										text={topic.title}
										indexes={marked("topic", topic.id)}
									/>
								</h3>
								<span class={styles.weight}>
									{i18n.t().catalog.relevancy}{" "}
									{formatWeight(topic.relevancy, i18n.locale())}
								</span>
								<Show when={props.topicActions !== undefined}>
									<span class={styles.topicActions}>
										{props.topicActions?.(topic)}
									</span>
								</Show>
							</div>
							<Show
								when={
									topic.description !== undefined && topic.description !== null
								}
							>
								<p class={styles.topicDescription}>{topic.description}</p>
							</Show>
						</div>

						<Show
							when={
								showsBranch(topic, folding()) && topic.capabilities.length > 0
							}
						>
							<div class={styles.section}>
								<span class={styles.sectionTitle}>
									{i18n.t().catalog.capabilities}
								</span>
								<ul class={styles.list}>
									<For each={topic.capabilities}>
										{(capability) => (
											<li
												class={`${styles.item} ${styles.itemCapability}`}
												id={anchorId({
													kind: "capability",
													id: capability.id,
												})}
											>
												<span class={styles.itemTitle}>
													<Highlight
														text={capability.label}
														indexes={marked("capability", capability.id)}
													/>
												</span>
												<span class={styles.itemMeta}>
													{capability.questions.length}{" "}
													{i18n.t().catalog.questionsLinked}
												</span>
												<Show when={props.capabilityActions !== undefined}>
													<span class={styles.itemActions}>
														{props.capabilityActions?.(capability, topic)}
													</span>
												</Show>
											</li>
										)}
									</For>
								</ul>
							</div>
						</Show>

						<Show
							when={showsBranch(topic, folding()) && topic.questions.length > 0}
						>
							<div class={styles.section}>
								<span class={styles.sectionTitle}>
									{i18n.t().catalog.questions}
								</span>
								<ul class={styles.list}>
									<For each={topic.questions}>
										{(question) => (
											<li
												class={`${styles.item} ${styles.itemQuestion}`}
												id={anchorId({ kind: "question", id: question.id })}
											>
												<span class={styles.itemTitle}>
													<Highlight
														text={question.title}
														indexes={marked("question", question.id)}
													/>
												</span>
												<span
													class={`${styles.coverage} ${coverageStyle[question.coverage]}`}
													title={i18n.t().coverage.hint[question.coverage]}
												>
													{i18n.t().coverage.label[question.coverage]}
												</span>
												<Show when={question.informational}>
													<span class={styles.informational}>
														{i18n.t().catalog.informational}
													</span>
												</Show>
												<Show when={props.questionActions !== undefined}>
													<span class={styles.itemActions}>
														{props.questionActions?.(question, topic)}
													</span>
												</Show>
											</li>
										)}
									</For>
								</ul>
							</div>
						</Show>
					</li>
				)}
			</For>
		</ul>
	);

	return <Branch topics={props.topics} />;
};
